import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { selectAccountId } from '@common/store/selectors';

// Matches the actual AccountLocation API payload (`location_uuid`, not
// `uuid`). There is no `is_active` or `type` field on the backend model -
// those belong to Workplace's Location shape, not the local AccountLocation
// cache Kanga actually serializes here.
export interface AccountLocation {
  location_uuid: string;
  name: string;
}

interface LocationsResponse {
  results: AccountLocation[];
  count: number;
  next: string | null;
  previous: string | null;
}

// Same underlying endpoint/shape as `AccountLocation`, plus `id`/`account`/
// `is_default` for call sites that need them.
export interface AccountLocationDetail {
  id: number;
  account: string;
  location_uuid: string;
  name: string;
  is_default: boolean;
}

interface AccountLocationDetailsResponse {
  results: AccountLocationDetail[];
  count: number;
  next: string | null;
  previous: string | null;
}

export interface SearchLocationResponse {
  found_locations: { name: string; uuid: string }[];
  missing_locations: string[];
}

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  private readonly baseUrl = `${environment.apps.kanga}/api/v1/accounts`;

  private locations$: Observable<AccountLocation[]> | null = null;

  constructor(private http: HttpClient, private store: Store) {}

  getLocations(): Observable<AccountLocation[]> {
    if (!this.locations$) {
      this.locations$ = this.store.select(selectAccountId).pipe(
        filter((accountId) => !!accountId),
        take(1),
        switchMap((accountId) =>
          this.http.get<LocationsResponse>(
            `${this.baseUrl}/${accountId}/locations/`
          )
        ),
        // eslint-disable-next-line ngrx/avoid-mapping-selectors -- map is for HTTP response, not selector
        map((response) => response.results),
        shareReplay(1)
      );
    }
    return this.locations$;
  }

  getLocationsByAccount(
    accountId: string
  ): Observable<AccountLocationDetail[]> {
    return this.http
      .get<AccountLocationDetailsResponse>(
        `${this.baseUrl}/${accountId}/locations/`
      )
      .pipe(map((response) => response.results));
  }

  searchLocations(
    accountId: string,
    locationNames: string[]
  ): Observable<SearchLocationResponse> {
    return this.http.post<SearchLocationResponse>(
      `${this.baseUrl}/${accountId}/locations/search`,
      { locations: locationNames }
    );
  }

  clearCache(): void {
    this.locations$ = null;
  }
}
